import { useEffect } from "react";
import { z } from "zod";
import {
  MiDetailFields,
  MiIcon,
  MiList,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ICONS_WEB,
  INPUT_TYPES,
  MODAL_TITLES,
  PAGINATION,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { useIssueTemplateOrderTemplatesViewModel } from "./useIssueTemplateOrderTemplatesViewModel";

export const IssueTemplateOrderTemplates = ({
  issueTemplateId,
  stateManager,
  openAddModal,
  setOpenAddModal,
}) => {
  const theme = useTheme();

  const {
    selectedItem,
    setSelectedItem,
    isOpenDeleteModal,
    setIsOpenDeleteModal,
    getAllData,
    addIssueTemplateOrderTemplate,
    removeIssueTemplateOrderTemplate,
    issueTemplateOrderTemplates,
    orderTemplates,
  } = useIssueTemplateOrderTemplatesViewModel({
    issueTemplateId,
    stateManager,
  });

  useEffect(() => {
    getAllData();
  }, []);

  const onDelete = () => {
    removeIssueTemplateOrderTemplate({
      orderTemplateId: selectedItem.orderTemplateId,
      issueTemplateId: selectedItem.issueTemplateId,
      modifiedOn: selectedItem.modifiedOn,
    });
    handleCancelDelete();
  };

  const handleCancelDelete = () => {
    setIsOpenDeleteModal(false);
    setSelectedItem(null);
  };

  const handleAddOrderTemplate = () => {
    addIssueTemplateOrderTemplate(edit.edits);
    setOpenAddModal(false);
  };

  const handleAddModalClose = () => {
    edit.reset();
    setOpenAddModal(false);
  };

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setIsOpenDeleteModal(true);
      },
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          size={1}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  const addModalFields = [
    {
      label: `${ENTITY_NAMES.ORDER_TEMPLATE} Name`,
      name: "orderTemplateId",
      fieldType: INPUT_TYPES.SELECT,
      options: orderTemplates,
      width: "100%",
      required: true,
      validationRule: z.string().min(0, "Product is required"),
    },
  ];

  const orderTemplatesList = [
    {
      field: "name",
      headerName: `${ENTITY_NAMES.ORDER_TEMPLATE}`,
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
  ];

  const edit = useEdit({ orderTemplateId: "" }, addModalFields);

  return (
    <Box height="100%">
      <MiList
        columns={orderTemplatesList}
        data={issueTemplateOrderTemplates}
        paginationMode={PAGINATION.CLIENT}
        loading={stateManager.isBusy()}
        disableRowSelectionOnClick
        getRowActions={getRowActions}
        data-testid="issue-template-products-table"
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        description={`Are you sure you want to delete ${selectedItem?.name}?`}
        open={isOpenDeleteModal}
        handlePositive={onDelete}
        handleNegative={handleCancelDelete}
        textPositive={BUTTON_LABELS.DELETE}
      />
      <MiModal
        open={openAddModal}
        closeModal={handleAddModalClose}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            size: "medium",
            color: BUTTON_COLORS.BLACK,
            onClick: handleAddModalClose,
          },
          {
            name: `Add ${ENTITY_NAMES.ORDER_TEMPLATE}`,
            variant: BUTTON_VARIANTS.CONTAINED,
            size: "medium",
            color: BUTTON_COLORS.PRIMARY,
            onClick: handleAddOrderTemplate,
            disabled: stateManager.isBusy() || !edit.isValid(),
          },
        ]}
        title={`Add ${ENTITY_NAMES.ORDER_TEMPLATE} to Issue Template`}
        topRightClose={handleAddModalClose}
        loading={stateManager.isBusy()}
      >
        <MiDetailFields detailFields={addModalFields} edit={edit} />
      </MiModal>
    </Box>
  );
};
